import Alert from '@mui/material/Alert'
import CircularProgress from '@mui/material/CircularProgress'
import Icon from '@mui/material/Icon'
import React from 'react'
import { DropzoneRef } from 'react-dropzone'

import Uploader from '@/app/component/atom/upload'
import { FileUploadType, PersonalizationType } from '@/app/types'
import config from '@/config'

import {
  Caption,
  EditBtn,
  EditOverlay,
  PlayBtn,
  PlayContainer,
  PlayOverlay,
  PreviewImage,
  RelativeContainer,
  UploadContainer,
} from './common'
import { videoAcceptList, videoExtensionList } from './media-types'

type Props = {
  canRegisterTemplate: boolean
  enableMediaCaption: boolean
  files: Record<string, any>
  personalizationList: PersonalizationType
  text: string
  video: string

  changeHandler: (value: { text: string }) => void
  getFile: (path: string) => Promise<any>
  saveHandler: (file: FileUploadType) => Promise<void>
}

const VideoMessage: React.FC<Props> = ({
  canRegisterTemplate,
  enableMediaCaption,
  files,
  personalizationList,
  text,
  video,

  changeHandler,
  getFile,
  saveHandler,
}) => {
  const videoUploadRef = React.useRef<DropzoneRef>(null)
  const videoPlayerRef = React.useRef<HTMLVideoElement>(null)
  const previousVideoRef = React.useRef('')
  const pendingUploadRef = React.useRef<FileUploadType | null>(null)
  const [uploading, setUploading] = React.useState(false)
  const [uploadError, setUploadError] = React.useState(false)
  const [videoLoading, setVideoLoading] = React.useState(false)
  const [videoFile, setVideoFile] = React.useState('')
  const [videoError, setVideoError] = React.useState(false)

  React.useEffect(() => {
    // getFile can change identity on rerenders; request only when the video changes.
    if (previousVideoRef.current === video) {
      return
    }
    previousVideoRef.current = video
    setVideoFile('')
    if (video) {
      getFile(`thumb/${video}`)
    }
  }, [video, getFile])

  React.useEffect(
    () => () => {
      if (pendingUploadRef.current) {
        pendingUploadRef.current.cancelled = true
        pendingUploadRef.current = null
      }
    },
    [],
  )

  React.useEffect(() => {
    if (videoError) {
      setTimeout(() => {
        setVideoError(false)
      }, 3000)
    }
  }, [videoError])

  const downloadVideo = React.useCallback(async () => {
    setVideoLoading(true)
    const videoURL = await getFile(video)
    if (videoURL instanceof Error) {
      setVideoError(true)
    } else {
      setVideoFile(videoURL.url)
      setTimeout(async () => {
        const videoPlayer = videoPlayerRef.current
        if (!videoPlayer) {
          return
        }

        try {
          await videoPlayer.play()
        } catch {
          if (videoPlayerRef.current === videoPlayer) {
            setVideoError(true)
          }
        }
      }, config.debounce.time)
    }
    setVideoLoading(false)
  }, [video, getFile])

  const cancelUpload = () => {
    if (pendingUploadRef.current) {
      pendingUploadRef.current.cancelled = true
      pendingUploadRef.current = null
    }
    setUploading(false)
  }

  return (
    <>
      <UploadContainer style={{ display: !video || uploading ? 'flex' : 'none' }}>
        <RelativeContainer>
          <Uploader
            accept={videoAcceptList}
            currentFile={videoFile}
            extensions={videoExtensionList}
            files={files}
            maxFileSize="16mb"
            namespace="whatsapp-video"
            uploading={uploading}
            uploadRef={videoUploadRef}
            onAccept={async ({ file }: { file: File }) => {
              if (pendingUploadRef.current) {
                pendingUploadRef.current.cancelled = true
              }
              const upload: FileUploadType = {
                cancelled: false,
                file,
                filename: file.name,
                type: 'video',
              }
              pendingUploadRef.current = upload
              setUploading(true)
              setUploadError(false)
              try {
                await saveHandler(upload)
              } catch {
                if (!upload.cancelled) {
                  setUploadError(true)
                }
              } finally {
                if (pendingUploadRef.current === upload) {
                  pendingUploadRef.current = null
                  setUploading(false)
                }
              }
            }}
            onCancel={cancelUpload}
          />
        </RelativeContainer>
      </UploadContainer>
      <UploadContainer style={{ display: video && !uploading ? 'flex' : 'none' }}>
        <RelativeContainer>
          {videoLoading ? (
            <div>
              <CircularProgress />
            </div>
          ) : (
            <>
              {videoFile ? (
                <video ref={videoPlayerRef} width="400" height="200" controls src={videoFile} />
              ) : (
                <>
                  <PreviewImage
                    src={files?.[`thumb/${video}`]?.url || ''}
                    alt="Video Preview"
                    onError={(e: any) => {
                      e.target.onerror = null
                      e.target.src = '/static/no-preview-available.svg'
                    }}
                  />
                  <PlayOverlay>
                    <PlayContainer>
                      <PlayBtn id="whatsapp-video-play-btn" onClick={downloadVideo}>
                        <Icon>play_arrow</Icon>
                      </PlayBtn>
                    </PlayContainer>
                  </PlayOverlay>
                </>
              )}
              <EditOverlay>
                <EditBtn
                  id="whatsapp-edit-btn"
                  variant="outlined"
                  size="small"
                  color="primary"
                  onClick={() => videoUploadRef.current?.open()}
                  startIcon={<Icon>edit</Icon>}
                >
                  Replace
                </EditBtn>
              </EditOverlay>
              {videoError && <Alert severity="warning">Couldn't load the video. Please try again later.</Alert>}
            </>
          )}
        </RelativeContainer>
      </UploadContainer>
      {uploadError && <Alert severity="error">Couldn't upload the video. Please try again.</Alert>}
      <Caption
        enableMediaCaption={enableMediaCaption && (!video || canRegisterTemplate)}
        personalizationList={personalizationList}
        text={text}
        onChange={(updatedValue) => {
          changeHandler({
            text: updatedValue,
          })
        }}
      />
    </>
  )
}

export default VideoMessage

export type { Props as VideoMessageProps }
